import { useCallback, useEffect, useMemo, useState } from 'react';
import { hashUserId } from '~utils/hash';
import {
  ClaimCodeFiltersMap,
  ContextFiltersMap,
  CustomerFiltersMap,
  FilterContext,
  FilterOptionPersisted,
  STORAGE_KEY,
  WaveFiltersStorage,
} from './persistentFiltersModel';

// Re-export FilterContext for use in other components
export { FilterContext };

// ===========================================================
// STORAGE
// ===========================================================
function loadStorage(): WaveFiltersStorage {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as WaveFiltersStorage) : {};
  } catch {
    return {};
  }
}

export function clearWarningFilter() {
  const storage = loadStorage();
  let isUpdated = false;

  for (const userId in storage) {
    const userFilters = storage[userId];

    for (const customerId in userFilters) {
      const customerData = userFilters[customerId];

      if (customerData.displays && Array.isArray(customerData.displays.warnings)) {
        delete customerData.displays.warnings;
        isUpdated = true;
      }
    }
  }

  if (isUpdated) {
    saveStorage(storage);
  }
}

function saveStorage(data: WaveFiltersStorage) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function clearAllPersistentFilters() {
  window.localStorage.removeItem(STORAGE_KEY);
}

/**
 * Update the customerHandle in localStorage when it changes (e.g., in customer settings).
 * This ensures filters remain accessible after a handle change.
 */
export async function updateCustomerHandleInStorage(
  userId: string,
  customerId: string,
  newHandle: string,
): Promise<void> {
  if (!userId || !customerId || !newHandle) return;

  try {
    const hashedUserId = await hashUserId(userId);
    const storage = loadStorage();
    const userFilters = storage[hashedUserId];

    if (!userFilters || !userFilters[customerId]) return;

    // Update the customerHandle for this customer
    userFilters[customerId] = {
      ...userFilters[customerId],
      customerHandle: newHandle,
    };

    saveStorage(storage);
  } catch {
    // Silently fail - filter persistence is not critical
  }
}

// ===========================================================
// GET persisted filters for a user + customer + context
// ===========================================================
export async function getPersistentFilters(
  userId: string,
  customerHandle: string,
  context: FilterContext,
  claimCodeId?: string,
): Promise<CustomerFiltersMap> {
  if (!userId || !customerHandle) return {};

  try {
    const hashedUserId = await hashUserId(userId);
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const storage = JSON.parse(raw) as WaveFiltersStorage;
    const userFilters = storage[hashedUserId];
    if (!userFilters) return {};

    // Find customerId by matching _customerHandle
    let customerId: string | undefined;
    for (const [id, data] of Object.entries(userFilters)) {
      if (data?.customerHandle === customerHandle) {
        customerId = id;
        break;
      }
    }
    if (!customerId) return {};

    const customerData = userFilters[customerId];

    if (context === FilterContext.CLAIM_CODES_DISPLAY) {
      if (!claimCodeId) return {};
      const claimCodeMap = customerData?.[context] as ClaimCodeFiltersMap | undefined;
      return claimCodeMap?.[claimCodeId] ?? {};
    }

    return customerData?.[context] ?? {};
  } catch {
    return {};
  }
}

// ===========================================================
// TYPES
// ===========================================================
export type ValidFilterMap = Record<string, { all: FilterOptionPersisted[] }>;

// ===========================================================
// HOOK
// ===========================================================
/**
 * Hook for loading and saving persistent filters.
 * Uses customerId as the storage key (stable).
 * Uses customerHandle for quick lookup via _handleIndex.
 * Stores _customerHandle as metadata for reference/migration.
 */
export function usePersistentFilters(
  userId: string,
  customerHandle: string,
  context: FilterContext,
  customerId?: string, // Required for saving, optional for initial load
  claimCodeId?: string, // Required when context is CLAIM_CODES_DISPLAY
) {
  const [hashedUserId, setHashedUserId] = useState<string>('');
  const [customerFilters, setCustomerFilters] = useState<CustomerFiltersMap>({});
  const [isLoading, setIsLoading] = useState(true);
  // Hash the userId when it changes
  useEffect(() => {
    let isCancelled = false;
    setIsLoading(true);

    async function hashAndLoadFilters() {
      if (!userId || !customerHandle) {
        setHashedUserId('');
        setCustomerFilters({});
        setIsLoading(false);
        return;
      }

      const hashed = await hashUserId(userId);
      if (isCancelled) return;

      setHashedUserId(hashed);

      // Load filters from storage
      const storage = loadStorage();
      const userFilters = storage[hashed];

      if (!userFilters) {
        setCustomerFilters({});
        setIsLoading(false);
        return;
      }

      // Find customerId by matching _customerHandle, or use provided customerId
      let resolvedCustomerId = customerId;
      if (!resolvedCustomerId) {
        // Search for customer with matching handle
        for (const [id, data] of Object.entries(userFilters)) {
          if (data?.customerHandle === customerHandle) {
            resolvedCustomerId = id;
            break;
          }
        }
      }

      if (!resolvedCustomerId) {
        setCustomerFilters({});
        setIsLoading(false);
        return;
      }

      const customerData = userFilters[resolvedCustomerId];

      if (context === FilterContext.CLAIM_CODES_DISPLAY) {
        if (!claimCodeId) {
          setCustomerFilters({});
          setIsLoading(false);
          return;
        }
        const claimCodeMap = customerData?.[context] as ClaimCodeFiltersMap | undefined;
        setCustomerFilters(claimCodeMap?.[claimCodeId] ?? {});
      } else {
        setCustomerFilters(customerData?.[context] ?? {});
      }

      setIsLoading(false);
    }

    hashAndLoadFilters();

    return () => {
      isCancelled = true;
    };
  }, [userId, customerHandle, context, customerId, claimCodeId]);

  /** update a single column */
  const setColumnFilters = useCallback(
    async (columnId: string, values: FilterOptionPersisted[]) => {
      if (!hashedUserId || !customerId) return; // customerId required for saving

      // Only persist the ID — labels are resolved from backend data on restore
      const stripped: FilterOptionPersisted[] = values.map(({ id }) => ({ id }));

      const next = loadStorage();
      const userFilters = next[hashedUserId] ?? {};

      // Get existing customer data
      const customerContexts = userFilters[customerId] ?? {
        [FilterContext.DISPLAYS]: {},
        [FilterContext.CLAIM_CODES]: {},
        [FilterContext.CONFIGURATION]: {},
        [FilterContext.CLAIM_CODES_DISPLAY]: {},
      };

      if (context === FilterContext.CLAIM_CODES_DISPLAY) {
        if (!claimCodeId) return;
        const existingClaimCodeMap = (customerContexts[context] ?? {}) as ClaimCodeFiltersMap;
        const existingClaimFilters = { ...(existingClaimCodeMap[claimCodeId] ?? {}) };
        if (stripped.length === 0) {
          delete existingClaimFilters[columnId];
        } else {
          existingClaimFilters[columnId] = stripped;
        }
        const updatedClaimFilters = existingClaimFilters;
        next[hashedUserId] = {
          ...userFilters,
          [customerId]: {
            ...customerContexts,
            customerHandle,
            [context]: { ...existingClaimCodeMap, [claimCodeId]: updatedClaimFilters },
          },
        };
        saveStorage(next);
        const updatedClaimCodeMap = next[hashedUserId][customerId][context] as ClaimCodeFiltersMap;
        setCustomerFilters(updatedClaimCodeMap?.[claimCodeId] ?? {});
        return;
      }

      const currentContext = customerContexts[context] ?? {};

      // If values is empty, remove the column entirely
      if (stripped.length === 0) {
        const remainingFilters = { ...currentContext };
        delete remainingFilters[columnId];
        next[hashedUserId] = {
          ...userFilters,
          [customerId]: {
            ...customerContexts,
            customerHandle: customerHandle,
            [context]: remainingFilters,
          },
        };
      } else {
        // Otherwise, set the values
        next[hashedUserId] = {
          ...userFilters,
          [customerId]: {
            ...customerContexts,
            customerHandle: customerHandle,
            [context]: {
              ...currentContext,
              [columnId]: stripped,
            },
          },
        };
      }

      saveStorage(next);

      // Update local state
      const updatedCustomerData = next[hashedUserId][customerId];
      setCustomerFilters(updatedCustomerData?.[context] ?? {});
    },
    [hashedUserId, customerId, context, customerHandle, claimCodeId],
  );

  /** wipe filters only for this customer context */
  const resetCustomerFilters = useCallback(async () => {
    if (!hashedUserId) return;

    const next = loadStorage();
    const userFilters = next[hashedUserId];
    if (!userFilters) return;

    // Find customerId: use provided one or search by handle
    let resolvedCustomerId = customerId;
    if (!resolvedCustomerId) {
      for (const [id, data] of Object.entries(userFilters)) {
        if (data?.customerHandle === customerHandle) {
          resolvedCustomerId = id;
          break;
        }
      }
    }
    if (!resolvedCustomerId || !userFilters[resolvedCustomerId]) return;

    const customerData = userFilters[resolvedCustomerId] as ContextFiltersMap;

    if (context === FilterContext.CLAIM_CODES_DISPLAY) {
      if (!claimCodeId) return;
      const existingClaimCodeMap = (customerData[context] ?? {}) as ClaimCodeFiltersMap;
      next[hashedUserId] = {
        ...userFilters,
        [resolvedCustomerId]: {
          ...customerData,
          [context]: { ...existingClaimCodeMap, [claimCodeId]: {} },
        },
      };
      saveStorage(next);
      setCustomerFilters({});
      return;
    }

    next[hashedUserId] = {
      ...userFilters,
      [resolvedCustomerId]: {
        ...customerData,
        [context]: {},
      },
    };
    saveStorage(next);

    // Update local state
    setCustomerFilters({});
  }, [hashedUserId, customerId, customerHandle, context, claimCodeId]);

  /** clear all filters except warnings */
  const clearFiltersExceptWarnings = useCallback(async () => {
    if (!hashedUserId) return;

    const next = loadStorage();
    const userFilters = next[hashedUserId];
    if (!userFilters) return;

    // Find customerId: use provided one or search by handle
    let resolvedCustomerId = customerId;
    if (!resolvedCustomerId) {
      for (const [id, data] of Object.entries(userFilters)) {
        if (data?.customerHandle === customerHandle) {
          resolvedCustomerId = id;
          break;
        }
      }
    }
    if (!resolvedCustomerId || !userFilters[resolvedCustomerId]) return;

    const customerData = userFilters[resolvedCustomerId] as ContextFiltersMap;

    if (context === FilterContext.CLAIM_CODES_DISPLAY) {
      if (!claimCodeId) return;
      const existingClaimCodeMap = (customerData[context] ?? {}) as ClaimCodeFiltersMap;
      next[hashedUserId] = {
        ...userFilters,
        [resolvedCustomerId]: {
          ...customerData,
          [context]: { ...existingClaimCodeMap, [claimCodeId]: {} },
        },
      };
      saveStorage(next);
      setCustomerFilters({});
      return;
    }

    const currentFilters = (customerData[context] ?? {}) as CustomerFiltersMap;
    const warningsFilter = currentFilters['warnings'];

    next[hashedUserId] = {
      ...userFilters,
      [resolvedCustomerId]: {
        ...customerData,
        [context]: warningsFilter ? { warnings: warningsFilter } : {},
      },
    };
    saveStorage(next);

    // Update local state
    const updatedCustomerData = next[hashedUserId][resolvedCustomerId] as ContextFiltersMap;
    setCustomerFilters((updatedCustomerData?.[context] as CustomerFiltersMap) ?? {});
  }, [hashedUserId, customerId, customerHandle, context, claimCodeId]);

  return {
    customerFilters,
    isLoading,
    setColumnFilters,
    resetCustomerFilters,
    clearFiltersExceptWarnings,
    clearAllPersistentFilters,
  };
}

// ===========================================================
// CENTRALIZED FILTER PERSISTENCE MANAGER
// ===========================================================

export interface FilterPersistenceConfig<TFilter extends FilterOptionPersisted> {
  /** User ID for localStorage key */
  userId: string;
  /** Customer handle from URL (e.g., "blr-ic") - available immediately without GraphQL */
  customerHandle: string;
  /** Optional: Customer ID from GraphQL - stored as metadata for future handle migration */
  customerId?: string;
  /** Required when context is CLAIM_CODES_DISPLAY - scopes filters to this claim code */
  claimCodeId?: string;
  /** Filter context (displays, claimCodes, or configuration) */
  context: FilterContext;
  /** All currently enabled filters (for reconstruction from localStorage) */
  allEnabledFilters: TFilter[];
  /** Map of filter keys to valid backend IDs (for repair logic) */
  backendMap: Record<string, Set<string>>;
  /** Optional: Only process these filter keys (e.g., ['groups', 'site'] for claim code page) */
  allowedFilterKeys?: string[];
  /** Callback to apply filters to the UI (e.g., table.setFilter or handleSetFilters) */
  onApplyFilters: (columnId: string, filters: TFilter[], resetPagination?: boolean) => void;
  /** Optional: Check if a filter state exists (e.g., table.state.filters.some) */
  filterStateExists?: (columnId: string) => boolean;
  /** Optional: Check if filters are ready (e.g., data loaded) */
  filtersReady?: boolean;
}

export interface FilterPersistenceResult {
  /** Whether initial filters have been loaded from localStorage */
  isReady: boolean;
  /** Create a filter handler that persists changes */
  createFilterHandler: <TFilter extends FilterOptionPersisted>() => (
    filterId: string,
    newValue: TFilter[],
  ) => void;
}

/**
 * Centralized hook for managing filter persistence across display table, server-side filters, and claim codes.
 * Handles initial loading, repair, and persistence of filters to localStorage.
 * Uses customerHandle (from URL) instead of customerId to avoid waiting for GraphQL response.
 * Stores customerId as metadata for future-proofing when handles change.
 */
export function useFilterPersistenceManager<TFilter extends FilterOptionPersisted>({
  userId,
  customerHandle,
  customerId,
  claimCodeId,
  context,
  allEnabledFilters,
  backendMap,
  allowedFilterKeys,
  onApplyFilters,
  filterStateExists,
  filtersReady = true,
}: FilterPersistenceConfig<TFilter>): FilterPersistenceResult {
  const [initialFiltersApplied, setInitialFiltersApplied] = useState(false);

  const { customerFilters, isLoading, setColumnFilters } = usePersistentFilters(
    userId,
    customerHandle,
    context,
    customerId,
    claimCodeId,
  );

  // Reset state when user, customer, context, or claim code changes
  useEffect(() => {
    setInitialFiltersApplied(false);
  }, [userId, customerHandle, context, claimCodeId]);

  // Filter customerFilters by allowed keys if specified
  const filteredCustomerFilters = useMemo(() => {
    if (!allowedFilterKeys) return customerFilters;
    return Object.fromEntries(
      Object.entries(customerFilters).filter(([key]) => allowedFilterKeys.includes(key)),
    );
  }, [customerFilters, allowedFilterKeys]);

  // Apply initial filters from localStorage
  useEffect(() => {
    // Wait for filters to finish loading
    if (isLoading) {
      return;
    }

    if (initialFiltersApplied) {
      return;
    }

    // If no persisted filters, mark as ready immediately
    if (Object.keys(filteredCustomerFilters).length === 0) {
      setInitialFiltersApplied(true);
      return;
    }

    Object.entries(filteredCustomerFilters).forEach(([columnId, stored]) => {
      if (filterStateExists?.(columnId)) {
        return;
      }

      const fullOptions: TFilter[] = stored.map((v) => {
        const full = allEnabledFilters.find((o) => o.id === v.id);
        if (full) return full;

        return { value: v.id, column: columnId, id: v.id } as unknown as TFilter;
      });

      // Pass false to avoid resetting pagination on initial load from localStorage
      onApplyFilters(columnId, fullOptions, false);
    });

    setInitialFiltersApplied(true);
  }, [
    filteredCustomerFilters,
    allEnabledFilters,
    initialFiltersApplied,
    onApplyFilters,
    filterStateExists,
    isLoading,
  ]);

  // Repair filters when data is loaded - remove IDs that no longer exist in the backend
  // Run whenever backendMap changes (on every poll) to enable live filter updates
  useEffect(() => {
    if (!filtersReady || Object.keys(filteredCustomerFilters).length === 0) {
      return;
    }

    Object.entries(filteredCustomerFilters).forEach(([columnId, storedValues]) => {
      const backendIds = backendMap[columnId];
      if (!backendIds) return;

      const repaired = storedValues.filter((item) => backendIds.has(item.id));

      if (repaired.length !== storedValues.length) {
        void setColumnFilters(columnId, repaired);

        const fullOptions = repaired.map((v) => {
          const current = allEnabledFilters.find((o) => o.id === v.id);
          return current ?? ({ value: v.id, id: v.id, column: columnId } as unknown as TFilter);
        });

        onApplyFilters(columnId, fullOptions, false);
      }
    });
  }, [
    filtersReady,
    filteredCustomerFilters,
    backendMap,
    allEnabledFilters,
    setColumnFilters,
    onApplyFilters,
  ]);

  // Create a filter handler that persists changes
  const createFilterHandler = useCallback(() => {
    return <T extends FilterOptionPersisted>(filterId: string, newValue: T[]) => {
      // User-initiated filter changes should reset pagination
      onApplyFilters(filterId, newValue as unknown as TFilter[], true);
      // setColumnFilters is async but we don't need to await it here
      // The persistence happens in the background
      void setColumnFilters(filterId, newValue);
    };
  }, [onApplyFilters, setColumnFilters]);

  return {
    isReady: initialFiltersApplied,
    createFilterHandler,
  };
}
